import React from "react";
import { formatConnectCode } from "helpers/formatConnectCode";
import FormError from "ui/FormError";
import Form from "ui/Form";
import Input from "ui/Input";
import Typography from "ui/Typography";
import Button from "ui/Button";
import styled from "styled-components/macro";
import * as yup from "yup";
import { useFormik } from "formik";
import { ActionType, EventName } from "utils/analytics/analyticsConstants";
import { useAnalytics } from "utils/analytics/AnalyticsContext";

const $Form = styled(Form)`
  text-align: left;
  margin-bottom: 1.5rem;
  min-width: 18rem;
  max-width: 18rem;
`;

const $Input = styled(Input)`
  &:not(:placeholder-shown) {
    text-transform: uppercase;
  }
`;

const $TextContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const validationSchema = yup.object().shape({
  connectCode: yup
    .string()
    .required("Please enter a valid class code.")
    .matches(
      /^[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}$/,
      "Please enter a valid class code.",
    ),
});

export interface ConnectCodeFormProps {
  onSubmit: (code: string) => void;
  onChange: () => void;
  processing: boolean;
  error: string;
}

export default function ConnectCodeForm(props: ConnectCodeFormProps) {
  const { trackEvent } = useAnalytics("StudentConnectCodeForm");
  const formik = useFormik({
    initialValues: {
      connectCode: "",
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      props.onSubmit(values.connectCode);
    },
  });

  const isInvalidCode = !!formik.errors.connectCode || !!props.error;

  async function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Clears errors from submitting to API and formik validation
    props.onChange();
    formik.setErrors({});

    await formik.setFieldValue(
      "connectCode",
      formatConnectCode(e.target.value),
    );
  }

  return (
    <$Form onSubmit={formik.handleSubmit}>
      <Form.Group controlId="formConnectCode">
        <Form.Label>
          <Typography size="subheading">Enter code</Typography>
        </Form.Label>
        <$Input
          type="text"
          name="connectCode"
          placeholder="Type class code"
          maxLength={9}
          value={formik.values.connectCode}
          onChange={onChange}
          onBlur={formik.handleBlur}
          onFocus={() => {
            trackEvent({
              eventName:
                EventName.StudentConnectCodeSignupPage
                  .FOCUSED_TYPE_CONNECT_CODE,
              eventType: ActionType.FOCUS,
            });
          }}
          required
          aria-invalid={isInvalidCode}
          aria-describedby="connectCodeError"
        />
        <FormError id="connectCodeError" isInvalid={isInvalidCode}>
          {formik.errors.connectCode || props.error}
        </FormError>
      </Form.Group>
      <Button
        block
        disabled={!formik.values.connectCode || isInvalidCode}
        analyticsName={EventName.StudentConnectCodeSignupPage.CLICKED_SIGN_UP}
        variant="primary"
        type="submit"
        requestInProgress={props.processing}
      >
        Join
      </Button>
      <$TextContainer>
        <Typography size="bodySansSerif" fontWeight="bold">
          Don’t have a class code?
        </Typography>
        <Typography size="bodySansSerif">
          Contact your teacher for support.
        </Typography>
      </$TextContainer>
    </$Form>
  );
}
